import React, { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Loading } from '../shared';

interface NetDocsFilePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesDownloaded: (files: File[]) => void;
  accessKey: string;
  region: string;
  stage: boolean;
}

// Type declaration for the nd-connect custom element
declare global {
  interface NDConnectElement extends HTMLElement {
    setAccessKey: (key: string) => void;
  }
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'nd-connect': React.DetailedHTMLProps<React.HTMLAttributes<NDConnectElement> & { 'access-key'?: string }, NDConnectElement>;
    }
  }
}

const ND_CONNECT_SCRIPT_ID = 'nd-connect-script';

function getScriptUrl(region: string, stage: boolean): string {
  const subdomain = stage ? 'stage' : region;
  return `https://web-api.${subdomain}.netdocuments.app/isv-api/web-component/nd-connect.js`;
}

/** Load the ND Connect script for the given region. No-ops if already loaded for that region. */
function ensureScript(region: string, stage: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(ND_CONNECT_SCRIPT_ID) as HTMLScriptElement | null;
    const url = getScriptUrl(region, stage);

    // Already loaded for this region
    if (existing && existing.src === url) {
      resolve();
      return;
    }

    // Different region — remove old script (component will re-register on next load)
    if (existing) {
      existing.remove();
    }

    const script = document.createElement('script');
    script.id = ND_CONNECT_SCRIPT_ID;
    script.type = 'module';
    script.crossOrigin = 'anonymous';
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ND Connect script for region "${region}"`));
    document.body.appendChild(script);
  });
}

export const NetDocsFilePicker: React.FC<NetDocsFilePickerProps> = ({
  isOpen,
  onClose,
  onFilesDownloaded,
  accessKey,
  region,
  stage,
}) => {
  const componentRef = useRef<NDConnectElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onFilesDownloadedRef = useRef(onFilesDownloaded);
  onFilesDownloadedRef.current = onFilesDownloaded;

  // Load the ND Connect script for this tenant's region when the dialog opens
  useEffect(() => {
    if (!isOpen) return;

    setScriptLoaded(false);
    ensureScript(region, stage)
      .then(() => setScriptLoaded(true))
      .catch(() => {
        toast.error('Failed to load NetDocuments component');
        onCloseRef.current();
      });
  }, [isOpen, region, stage]);

  // Set access key once script is loaded and component is defined
  useEffect(() => {
    if (!isOpen || !scriptLoaded || !accessKey) return;

    customElements.whenDefined('nd-connect').then(() => {
      componentRef.current?.setAccessKey(accessKey);
    });
  }, [isOpen, scriptLoaded, accessKey]);

  // Request downloads from ND Connect via PostMessage
  const requestDownload = (items: Array<{ docId: string; name: string; envId: string }>) => {
    window.postMessage({
      type: 'download-request',
      requestId: `download-${Date.now()}`,
      timestamp: Date.now(),
      data: {
        documents: items.map(({ docId, name, envId }) => ({ docId, name, envId })),
      },
    }, window.location.origin);
  };

  // Fetch file from download URL and convert to File object
  const fetchFileFromUrl = async (url: string, name: string): Promise<File> => {
    const { hostname } = new URL(url);
    if (!hostname.endsWith('.netdocuments.app') && !hostname.endsWith('.netdocuments.com')) {
      throw new Error('Invalid download URL origin');
    }
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
    const blob = await response.blob();
    return new File([blob], name, { type: blob.type || 'application/octet-stream' });
  };

  // Handle items-open event (user selected files) and download responses
  useEffect(() => {
    if (!isOpen) return;

    // Map docId → ext so we can restore extensions on downloaded files
    let itemExtensions: Record<string, string> = {};

    const handleItemsOpen = (event: Event) => {
      const { items } = (event as CustomEvent).detail;
      if (!items || items.length === 0) return;

      itemExtensions = {};
      for (const item of items) {
        if (item.ext) {
          itemExtensions[item.docId] = item.ext;
        }
      }

      setIsDownloading(true);
      requestDownload(items);
    };

    const handleCancel = () => {
      onCloseRef.current();
    };

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const { type, data } = event.data;

      if (type === 'download-response') {
        try {
          const successfulResults = data.results.filter(
            (r: { success: boolean; url?: string }) => r.success && r.url
          );

          if (successfulResults.length === 0) {
            toast.error('No files could be downloaded');
            setIsDownloading(false);
            return;
          }

          // Fetch all files in parallel from the download URLs
          const files = await Promise.all(
            successfulResults.map((result: { url: string; name: string; docId: string }) => {
              const ext = itemExtensions[result.docId];
              const fileName = ext && !result.name.endsWith(`.${ext}`)
                ? `${result.name}.${ext}`
                : result.name;
              return fetchFileFromUrl(result.url, fileName);
            })
          );

          onFilesDownloadedRef.current(files);
          onCloseRef.current();
        } catch (error) {
          console.error('Failed to fetch downloaded files:', error);
          toast.error('Failed to download files from NetDocuments');
        } finally {
          setIsDownloading(false);
        }
      }

      if (type === 'download-error') {
        console.error('NetDocuments download error:', data.code, data.error);
        const errorMessages: Record<string, string> = {
          TOKEN_EXPIRED: 'Session expired. Please try again.',
          DOCUMENT_NOT_FOUND: 'Document not found.',
          DOCUMENT_NOT_AUTHORIZED: 'You do not have permission to download this document.',
          DOWNLOAD_FAILED: 'Download failed. Please check your permissions.',
        };
        toast.error(errorMessages[data.code] || data.error || 'Download failed');
        setIsDownloading(false);
      }
    };

    document.addEventListener('items-open', handleItemsOpen);
    document.addEventListener('nd-connect-cancel', handleCancel);
    window.addEventListener('message', handleMessage);

    return () => {
      document.removeEventListener('items-open', handleItemsOpen);
      document.removeEventListener('nd-connect-cancel', handleCancel);
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen]);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-100000 bg-black/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content aria-describedby={undefined} className="fixed left-[50%] top-[50%] z-100001 max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] translate-x-[-50%] translate-y-[-50%] border border-stone-200 bg-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg overflow-hidden p-0">
          <DialogPrimitive.Title className="sr-only">NetDocuments File Picker</DialogPrimitive.Title>
          {isDownloading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
              <Loading text="Downloading files from NetDocuments..." />
            </div>
          )}
          {!scriptLoaded && !isDownloading && (
            <div className="flex items-center justify-center h-full">
              <Loading text="Loading NetDocuments..." />
            </div>
          )}
          {scriptLoaded && (
            <div className="w-full h-full">
              <nd-connect ref={componentRef} style={{ display: 'block', width: '100%', height: '100%' }} />
            </div>
          )}
          <DialogPrimitive.Close className="absolute right-3 top-3 z-10 rounded-xs p-1.5 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
